import { StatusMessage } from "@whitehaven/ui";
import { useRef, useSyncExternalStore } from "react";
import { focusTarget } from "./focus-management";
import {
  shouldShowInstallPrompt,
  type InstallPromptSnapshot,
} from "./install-prompt-model.ts";
import {
  portalInstallPrompt,
  type InstallPromptManager,
  type InstallPromptRequestResult,
} from "./install-prompt.ts";

interface PortalInstallNoticeProps {
  readonly manager?: InstallPromptManager;
}

interface InstallNoticeCopy {
  readonly title: string;
  readonly body: string;
}

export function getPortalInstallNoticeCopy(
  snapshot: InstallPromptSnapshot,
): InstallNoticeCopy {
  if (snapshot.status === "manual") {
    return {
      title: "Add Whitehaven to your home screen",
      body: "On iPhone or iPad, use Safari’s Share menu to add the portal.",
    };
  }

  if (snapshot.status === "error") {
    return {
      title: "Install options aren’t available",
      body: "You can keep using the portal in this browser and try again another time.",
    };
  }

  return {
    title: "Keep Whitehaven close",
    body: "Add this secure portal to your device for easier access. Private records still require a connection.",
  };
}

function shouldRestoreFocusAfterInstall(result: InstallPromptRequestResult) {
  return ["accepted", "dismissed", "installed"].includes(result);
}

export function PortalInstallNotice({
  manager = portalInstallPrompt,
}: PortalInstallNoticeProps) {
  const snapshot = useSyncExternalStore(
    manager.subscribe,
    manager.getSnapshot,
    manager.getSnapshot,
  );
  const noticeRef = useRef<HTMLElement>(null);

  if (!shouldShowInstallPrompt(snapshot)) {
    return null;
  }

  const copy = getPortalInstallNoticeCopy(snapshot);
  const prompting = snapshot.status === "prompting";
  const manual = snapshot.status === "manual";
  const failed = snapshot.status === "error";

  const returnFocusToPortal = () => {
    window.requestAnimationFrame(() => {
      focusTarget(document.querySelector("[data-main-focus]"), {
        scroll: false,
      });
    });
  };

  const beginInstall = () => {
    if (prompting || failed) {
      return;
    }
    const shouldRestore =
      noticeRef.current?.contains(document.activeElement) ?? false;
    void manager.requestInstall().then((result) => {
      if (shouldRestore && shouldRestoreFocusAfterInstall(result)) {
        returnFocusToPortal();
      }
    });
  };

  const dismissAndReturnFocus = () => {
    const shouldRestore =
      noticeRef.current?.contains(document.activeElement) ?? false;
    manager.dismiss();
    if (shouldRestore) {
      returnFocusToPortal();
    }
  };

  return (
    <aside
      ref={noticeRef}
      className="portal-install-notice"
      aria-label="Install Whitehaven"
      aria-busy={prompting ? "true" : undefined}
    >
      <StatusMessage
        className="portal-install-notice__message"
        tone={failed ? "warning" : "neutral"}
        title={copy.title}
        live={false}
        icon={<span aria-hidden="true">+</span>}
      >
        <p>{copy.body}</p>
        {manual && (
          <details className="portal-install-notice__instructions">
            <summary>How to install</summary>
            <ol>
              <li>Open this portal in Safari.</li>
              <li>
                Tap <strong>Share</strong>, then{" "}
                <strong>Add to Home Screen</strong>.
              </li>
              <li>
                Turn on <strong>Open as Web App</strong>, then tap{" "}
                <strong>Add</strong>.
              </li>
            </ol>
          </details>
        )}
        <div className="portal-install-notice__actions">
          {!manual && (
            <button
              type="button"
              className="portal-install-notice__button"
              aria-disabled={prompting || failed ? "true" : undefined}
              onClick={beginInstall}
            >
              {prompting
                ? "Opening install options\u2026"
                : failed
                  ? "Install unavailable"
                  : "Add to device"}
            </button>
          )}
          {!prompting && (
            <button
              type="button"
              className="portal-install-notice__button portal-install-notice__button--secondary"
              onClick={dismissAndReturnFocus}
            >
              {failed ? "Close" : "Not now"}
            </button>
          )}
        </div>
      </StatusMessage>
    </aside>
  );
}
